'use strict';

const BULLET_COLORS = ['#ff496c', '#ff9f43', '#b55cff', '#ffdf5d'];
const EDGE_ORDER = ['top', 'right', 'bottom', 'left'];
const OPPOSITE_EDGE = Object.freeze({
  top: 'bottom',
  right: 'left',
  bottom: 'top',
  left: 'right'
});

class PatternEmitter {
  constructor(config, bounds) {
    this.config = config;
    this.bounds = bounds;
    this.timer = 0.35;
    this.shotCount = 0;
    this.spiralAngle = 0;
    this.edgeCursor = 0;
  }

  update(dt, player, pool) {
    this.timer -= dt;
    if (this.timer > 0) return;
    this.timer += this.config.spawnInterval;
    const patterns = this.config.patterns;
    const pattern = patterns[this.shotCount % patterns.length];
    this.emit(pattern, player, pool);
    this.shotCount += 1;
  }

  emit(pattern, player, pool) {
    if (pattern === 'straight') this.emitStraight(player, pool);
    else if (pattern === 'aimed') this.emitAimed(player, pool);
    else if (pattern === 'fan') this.emitFan(player, pool);
    else if (pattern === 'spiral') this.emitSpiral(player, pool);
    else if (pattern === 'cross') this.emitCross(player, pool);
  }

  nextEdge() {
    const edge = EDGE_ORDER[this.edgeCursor % EDGE_ORDER.length];
    this.edgeCursor += 1;
    return edge;
  }

  pointOnEdge(edge) {
    const horizontalPadding = 55;
    const verticalPadding = 70;
    const outside = 20;
    if (edge === 'top') {
      return {
        edge,
        x: this.bounds.left + horizontalPadding +
          Math.random() * (this.bounds.right - this.bounds.left - horizontalPadding * 2),
        y: this.bounds.top - outside,
        inwardAngle: Math.PI / 2
      };
    }
    if (edge === 'right') {
      return {
        edge,
        x: this.bounds.right + outside,
        y: this.bounds.top + verticalPadding +
          Math.random() * (this.bounds.bottom - this.bounds.top - verticalPadding * 2),
        inwardAngle: Math.PI
      };
    }
    if (edge === 'bottom') {
      return {
        edge,
        x: this.bounds.left + horizontalPadding +
          Math.random() * (this.bounds.right - this.bounds.left - horizontalPadding * 2),
        y: this.bounds.bottom + outside,
        inwardAngle: -Math.PI / 2
      };
    }
    return {
      edge: 'left',
      x: this.bounds.left - outside,
      y: this.bounds.top + verticalPadding +
        Math.random() * (this.bounds.bottom - this.bounds.top - verticalPadding * 2),
      inwardAngle: 0
    };
  }

  angleToTarget(origin, player) {
    if (!player || !Number.isFinite(player.x) || !Number.isFinite(player.y)) {
      return origin.inwardAngle;
    }
    return Math.atan2(player.y - origin.y, player.x - origin.x);
  }

  spawnPolar(pool, origin, angle, speed, color, radius, turnMultiplier) {
    return pool.spawn({
      x: origin.x,
      y: origin.y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      color,
      radius: radius || 10,
      homing: true,
      turnRate: this.config.homingTurnRate * (turnMultiplier || 1)
    });
  }

  emitStraight(player, pool) {
    const origin = this.pointOnEdge(this.nextEdge());
    const angle = this.angleToTarget(origin, player) + (Math.random() - 0.5) * 0.22;
    this.spawnPolar(
      pool,
      origin,
      angle,
      this.config.bulletSpeed,
      BULLET_COLORS[0],
      10,
      0.82
    );
  }

  emitAimed(player, pool) {
    const origin = this.pointOnEdge(this.nextEdge());
    const angle = this.angleToTarget(origin, player);
    this.spawnPolar(
      pool,
      origin,
      angle,
      this.config.bulletSpeed,
      BULLET_COLORS[1],
      10,
      1
    );
  }

  emitFan(player, pool) {
    const origin = this.pointOnEdge(this.nextEdge());
    const count = this.config.level >= 38 ? 7 : 5;
    const centerAngle = this.angleToTarget(origin, player);
    const spread = Math.PI * 0.52;
    for (let i = 0; i < count; i += 1) {
      const ratio = count === 1 ? 0.5 : i / (count - 1);
      const angle = centerAngle - spread / 2 + spread * ratio;
      this.spawnPolar(
        pool,
        origin,
        angle,
        this.config.bulletSpeed * 0.78,
        BULLET_COLORS[2],
        9,
        0.72
      );
    }
  }

  emitSpiral(player, pool) {
    const origin = this.pointOnEdge(this.nextEdge());
    const centerAngle = this.angleToTarget(origin, player);
    const branches = this.config.level >= 60 ? 3 : 2;
    const waveOffset = Math.sin(this.spiralAngle) * 0.48;
    for (let i = 0; i < branches; i += 1) {
      const branchOffset = (i - (branches - 1) / 2) * 0.42;
      this.spawnPolar(
        pool,
        origin,
        centerAngle + waveOffset + branchOffset,
        this.config.bulletSpeed * 0.72,
        BULLET_COLORS[3],
        9,
        0.78
      );
    }
    this.spiralAngle += 0.58;
  }

  emitCross(player, pool) {
    const firstEdge = this.nextEdge();
    const secondEdge = OPPOSITE_EDGE[firstEdge];
    const firstOrigin = this.pointOnEdge(firstEdge);
    const secondOrigin = this.pointOnEdge(secondEdge);
    const firstAngle = this.angleToTarget(firstOrigin, player);
    const secondAngle = this.angleToTarget(secondOrigin, player);
    this.spawnPolar(
      pool,
      firstOrigin,
      firstAngle - 0.1,
      this.config.bulletSpeed * 0.86,
      BULLET_COLORS[2],
      11,
      0.86
    );
    this.spawnPolar(
      pool,
      firstOrigin,
      firstAngle + 0.1,
      this.config.bulletSpeed * 0.86,
      BULLET_COLORS[0],
      11,
      0.86
    );
    this.spawnPolar(
      pool,
      secondOrigin,
      secondAngle - 0.1,
      this.config.bulletSpeed * 0.86,
      BULLET_COLORS[0],
      11,
      0.86
    );
    this.spawnPolar(
      pool,
      secondOrigin,
      secondAngle + 0.1,
      this.config.bulletSpeed * 0.86,
      BULLET_COLORS[2],
      11,
      0.86
    );
  }
}

module.exports = PatternEmitter;
